import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import {
	Table,
	TableBody,
	TableHead,
	TableHeader,
	TableRow,
} from 'components/ui/table'
import Spinner from 'components/Spinner'
import {
	fetchVersions,
	softDeleteVersion,
} from 'slices/documentLibrarySlice'
import {
	getCurrentVersion,
	type DocumentLibraryState,
	type LibraryDocument,
	type UserSummary,
} from '../types/documentLibrary.types'
import { getDocumentIcon } from '../utils/getDocumentIcon'
import VersionHistoryRow from './VersionHistoryRow'

interface VersionHistoryDialogProps {
	open: boolean
	onClose: () => void
	documentId: string | null
	/** Wired by MYD-289 to open `DocumentShareDialog` for that version. */
	onShareVersion?: (documentId: string, versionId: string) => void
}

const COLUMNS: { id: string; label: string; align?: 'center' }[] = [
	{ id: 'owner', label: 'Owner' },
	{ id: 'fileDate', label: 'File Date' },
	{ id: 'version', label: 'Version', align: 'center' },
	{ id: 'changeNote', label: 'Change Note' },
	{ id: 'current', label: 'Current?', align: 'center' },
	{ id: 'share', label: 'Share', align: 'center' },
	{ id: 'download', label: 'Download', align: 'center' },
]

const noop = () => {}

const VersionHistoryDialog: React.FC<VersionHistoryDialogProps> = ({
	open,
	onClose,
	documentId,
	onShareVersion = noop,
}) => {
	const dispatch = useDispatch<any>()
	const document = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			documentId ? state.libraryDocuments.entities[documentId] : undefined,
	)
	const versions = useSelector(
		(state: { libraryDocuments: DocumentLibraryState }) =>
			documentId ? state.libraryDocuments.versionsByDocument[documentId] : undefined,
	)
	const userInfo = useSelector((state: any) => state.userLogin?.userInfo)

	useEffect(() => {
		if (open && documentId && !versions) {
			dispatch(fetchVersions({ id: documentId }))
		}
	}, [open, documentId, versions, dispatch])

	const ownerId = (() => {
		if (!document) return null
		const owner = (document as LibraryDocument).owner
		if (!owner) return null
		if (typeof owner === 'string') return owner
		return (owner as UserSummary)._id
	})()
	const canDelete =
		!!userInfo?._id && !!ownerId && String(userInfo._id) === String(ownerId)

	const handleSoftDelete = async (versionId: string) => {
		if (!documentId) return
		const ok = await toast.confirm({
			title: 'Delete version',
			message:
				'Delete this version? It will be hidden from the version history; the file remains in the audit trail.',
		})
		if (!ok) return
		dispatch(softDeleteVersion({ id: documentId, versionId }))
	}

	const isLoading = open && documentId && !versions

	const currentVersion = getCurrentVersion(document)
	const headerFilename =
		currentVersion?.originalFilename || document?.libraryName || ''
	const fileIcon = getDocumentIcon(headerFilename)

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="max-w-4xl">
				<DialogHeader>
					<div className="flex items-center gap-3">
						<div
							className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold text-[11px]"
							style={{ backgroundColor: fileIcon.color }}
						>
							{fileIcon.label}
						</div>
						<div>
							<DialogTitle>
								{document?.libraryName || 'Version history'}
							</DialogTitle>
							<DialogDescription>
								Every upload is preserved. Older versions remain
								downloadable from this list.
							</DialogDescription>
						</div>
					</div>
				</DialogHeader>

				<div className="px-5 py-4">
					{isLoading ? (
						<div className="py-12 flex items-center justify-center">
							<Spinner />
						</div>
					) : !versions || versions.length === 0 ? (
						<div className="py-12 text-center text-sm text-gray-500">
							No versions yet.
						</div>
					) : (
						<div className="rounded-lg border border-gray-200 max-h-[60vh] overflow-y-auto">
							<Table className="table-fixed w-full">
								<TableHeader>
									<TableRow className="border-b-2 border-[#0080fe] hover:bg-transparent">
										{COLUMNS.map(col => (
											<TableHead
												key={col.id}
												className={[
													'text-gray-700 text-xs font-semibold whitespace-nowrap',
													col.align === 'center' ? 'text-center' : '',
												].join(' ')}
											>
												{col.label}
											</TableHead>
										))}
									</TableRow>
								</TableHeader>
								<TableBody>
									{versions.map(v => (
										<VersionHistoryRow
											key={v._id}
											documentId={documentId as string}
											version={v}
											canDelete={canDelete}
											onShare={versionId =>
												onShareVersion(documentId as string, versionId)
											}
											onSoftDelete={handleSoftDelete}
										/>
									))}
								</TableBody>
							</Table>
						</div>
					)}
				</div>

				<DialogFooter>
					<Button type="button" onClick={onClose}>
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default VersionHistoryDialog
